import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { CleaningJobPanel, type CleaningJobPanelParts } from '@/components/cleaning/job-panel'
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { portfolioCalendarKey } from '@/hooks/use-portfolio-calendar'
import { propertyCalendarKey } from '@/hooks/use-property-calendar'

const SHEET_PARTS: CleaningJobPanelParts = {
  Header: SheetHeader,
  Title: SheetTitle,
  Body: SheetBody,
}

interface CleaningSheetProps {
  propertyId: string
  propertyName: string
  jobId: string
  /** The property's zone, from the calendar read; the job body falls back to its own. */
  timeZone: string
  onClose: () => void
}

/**
 * One cleaning job, opened from the property calendar.
 *
 * The body is the same `CleaningJobPanel` the cleaning board's dialog renders,
 * so every action it offers - assign, ack, confirm, not required, decide,
 * reschedule, pin, note, undo - is here, calling the same endpoints. What this
 * adds is the one thing the calendar needs: after an action lands the month
 * behind is refetched, so a moved cleaning moves on the grid and the grid only
 * ever shows what the API returned.
 *
 * Opened from a pasted link the job may not be in the visible month; the body
 * reads it by id and does not depend on the calendar.
 */
export function CleaningSheet({
  propertyId,
  propertyName,
  jobId,
  timeZone,
  onClose,
}: CleaningSheetProps) {
  const queryClient = useQueryClient()
  // Shares the cleaning board's key, so a visit to either warms the other.
  const teamsQuery = useQuery({
    queryKey: ['cleaning', 'teams'],
    queryFn: () => api.getCleaningTeams(),
  })

  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent aria-describedby={undefined} data-testid="cleaning-sheet">
        {/* The panel names the job's crew and offers the crews to assign, both from
            this list: opened before it lands it would show the crew's id and an
            empty pick list, then change under the operator's hand. */}
        {teamsQuery.isPending ? (
          <>
            <SheetHeader>
              <SheetTitle>Cleaning</SheetTitle>
            </SheetHeader>
            <SheetBody>
              <div className="space-y-3" aria-busy="true" data-testid="cleaning-sheet-loading">
                <LoadingSkeleton className="h-6 w-2/3" />
                <LoadingSkeleton className="h-40 w-full" />
              </div>
            </SheetBody>
          </>
        ) : (
          <CleaningJobPanel
            propertyId={propertyId}
            jobId={jobId}
            propertyName={propertyName}
            teams={teamsQuery.data?.teams ?? []}
            timeZone={timeZone}
            // The calendar read carries no "today"; without it days read as a
            // date ("Thu 16 Jul") rather than "Tomorrow", which is true either way.
            today={null}
            parts={SHEET_PARTS}
            onChanged={() => {
              void queryClient.invalidateQueries({ queryKey: propertyCalendarKey(propertyId) })
              // The portfolio calendar reads the same cleaning layer.
              void queryClient.invalidateQueries({ queryKey: portfolioCalendarKey() })
              // The board's lists are derived from the same job.
              void queryClient.invalidateQueries({ queryKey: ['cleaning', 'actions-needed'] })
              void queryClient.invalidateQueries({ queryKey: ['cleaning', 'jobs'] })
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
